import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column } from '@repo/ui/data-table'
import EditableInnerBox, { EditableInnerBoxTitle } from '@repo/ui/editable-box'
import {
  BackIcon,
  EmergencyPhoneIcon,
  FilterTwoIcon,
  GlobeIcon,
  PriceTagIcon
} from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API, ApiUrls, constructApiUrl } from '../../../api/apis.config'
import { getEnrollmentActions } from '../../../api/enrollments/get-enrollment-history'
import {
  defaultEnrollmentOfficialDocumentsAvailability,
  getEnrollmentOfficialDocumentsAvailability
} from '../../../api/enrollments/get-enrollment-official-documents-availability'
import {
  defaultEnrollmentStatus,
  getEnrollmentStatus
} from '../../../api/enrollments/get-enrollment-status'
import { getPaymentRecords } from '../../../api/enrollments/get-payment-records'
import {
  activateAutomaticPayments,
  deactivateAutomaticPayments
} from '../../../api/enrollments/update-automatic-payments'
import { useObserver } from '../../../lib/contexts/observer'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  EnrollmentActionTypeEnum,
  enrollmentActionTypeEnumMapping
} from '../../../lib/types/enums/enrollment/enrollment-action-type.enum'
import { paymentMeansEnumMapping } from '../../../lib/types/enums/payment-record/payment-means.enum'
import { PaymentTypeEnum } from '../../../lib/types/enums/payment-record/payment-type.enum'
import { StatusEnum, statusEnumMapping } from '../../../lib/types/enums/payment-record/status.enum'
import { paymentTransactionStatusEnum } from '../../../lib/types/enums/payments/payment-transaction-status.enum'
import {
  paymentRecordEnrollmentProfileMonitoringFields,
  PaymentRecordEnrollmentProfileMonitoringType
} from '../../../lib/types/fields/payment-records/payment-record'
import { EnrollmentHistoryField } from '../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentAction } from '../../../lib/types/models/enrollment-actions/enrollment-action'
import { PaymentRecord } from '../../../lib/types/models/payment-record/payment-record'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import ResourceAccessItem from '../client-space/resource-access-item'
import { EmailModal } from '../common/email-modal'
import EnrollmentPaymentRecord from '../enrollments/enrollment-form/enrollment-payment-record'
import EnrollmentPropositionSummary from '../enrollments/enrollment-form/enrollment-proposition-summary'
import { OtoIndividualEnrollmentPriceDetails } from '../enrollments/oto-individual-price-details'
import { mapEnrollmentPropositionToInput } from '../enrollments/utils/enrollments-utils'
import PaymentRecordStatusBadge from './shared/payment-record-status-badge'
import PropositionReductionForm from './shared/proposition-reduction-form'
import RefundEnrollmentForm, { REFUND_FAILED_MESSAGE } from './shared/refund-enrollment-form'
import TariffAdjustmentForm from './shared/tariff-adjustment-form'

const enrollmentFieldNames: Record<EnrollmentHistoryField, string> = {
  status: 'Statut',
  // child
  childGender: 'Genre',
  childBirthDate: 'Date de naissance',
  childNationality: 'Nationalité',
  childSchoolLevel: 'Niveau scolaire',
  childNumberOfYearsInLanguage: "Nombre d'années de langue",
  childPhoneNumber: 'Téléphone',
  childEmail: 'Email',
  // parent
  parent1Gender: 'Genre parent 1',
  parent1LastName: 'Nom parent 1',
  parent1FirstName: 'Prénom parent 1',
  parent1PhoneNumber: 'Téléphone parent 1',
  parent1Email: 'Email parent 1',
  parent1Job: 'Profession parent 1',
  parent1PhoneNumber2: 'Téléphone 2 parent 1',
  parent2Gender: 'Genre parent 2',
  parent2LastName: 'Nom parent 2',
  parent2FirstName: 'Prénom parent 2',
  parent2PhoneNumber: 'Téléphone parent 2',
  parent2Email: 'Email parent 2',
  parent2Job: 'Profession parent 2',
  parent2PhoneNumber2: 'Téléphone 2 parent 2',
  contactPriority: 'Priorité de contact',
  isAA: 'Prélèvement automatique',
  // address
  address1: 'Adresse 1',
  address2: 'Adresse 2',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  // person to contact in case of emergency
  personToContactInCaseOfEmergency: "Personne à contacter en cas d'urgence",
  // medical information
  medicalInformations: 'Informations médicales',
  medicalInformationsEn: 'Traduction des informations médicales',
  additionalMedicalInformations: 'Information complémentaire',
  additionalMedicalInformationsEn: "Traduction de l'information complémentaire",
  declaredFriends: 'Amis déclarés',
  'mainProposition.journey.reference': 'Séjour',
  'mainProposition.journey.mainOption.title': 'Option',
  'mainProposition.familyArrangement': "Modalité d'hébergement",
  'mainProposition.options': 'Options',
  'mainProposition.mainTrip': 'Voyage principal',
  'mainProposition.secondaryTrip': 'Voyage secondaire',
  refundAmount: 'Montant remboursé',
  refundStatus: 'Statut du remboursement',
  refundRequestedAmount: 'Montant demandé'
}

// a trip that is added or removed is saved with an empty value on that side: name it
const enrollmentFieldEmptyValues: Partial<Record<EnrollmentHistoryField, string>> = {
  'mainProposition.mainTrip': 'aucun',
  'mainProposition.secondaryTrip': 'aucun'
}

const EnrollmentProfileMonitoring = () => {
  const { enrollment, setEnrollment } = useEnrollment()
  const { downloadDocuments } = useDownloadDocuments()

  const { modalState, toggleModal, closeModel } = useModalManager([
    'summary',
    'refund',
    'tariffAdjustment',
    'email-notification'
  ])

  const { data: officialDocumentsAvailability } = useQuery({
    queryKey: ['enrollment-official-documents-availability', enrollment.id],
    queryFn: () => getEnrollmentOfficialDocumentsAvailability(enrollment.id),
    initialData: defaultEnrollmentOfficialDocumentsAvailability
  })

  const { notify } = useObserver()

  const { data: paymentRecords, refetch: refetchPaymentRecords } = useQuery({
    queryKey: ['payment-records', enrollment.id],
    queryFn: () =>
      getPaymentRecords(
        { enrollmentId: enrollment.id },
        paymentRecordEnrollmentProfileMonitoringFields
      )
  })

  const { data: actions, refetch: refetchActions } = useQuery({
    queryKey: ['enrollment-actions', enrollment.id],
    queryFn: () => getEnrollmentActions(enrollment.id)
  })

  const {
    mutate: activateAutomaticPaymentsMutation,
    isPending: isActivateAutomaticPaymentsPending
  } = useMutation({
    mutationFn: () => activateAutomaticPayments({ enrollmentId: enrollment.id }),
    onError: () => {
      setEnrollment({ ...enrollment, optinForAutomaticDebit: false })
    }
  })

  const {
    mutate: deactivateAutomaticPaymentsMutation,
    isPending: isDeactivateAutomaticPaymentsPending
  } = useMutation({
    mutationFn: () => deactivateAutomaticPayments({ enrollmentId: enrollment.id }),
    onError: () => {
      setEnrollment({ ...enrollment, optinForAutomaticDebit: true })
    }
  })

  const { data: enrollmentStatus, refetch: refetchEnrollmentStatus } = useQuery({
    enabled: !!enrollment.id,
    initialData: defaultEnrollmentStatus,
    queryKey: ['enrollment', enrollment.id, 'status'],
    queryFn: () => getEnrollmentStatus(enrollment.id)
  })

  useObserver({ key: 'enrollment-status', update: refetchEnrollmentStatus })
  useObserver({
    key: 'enrollment-profile-monitoring-payment-record',
    update: async () => {
      await refetchPaymentRecords()
      await refetchActions()
    }
  })

  const columns: Column<PaymentRecordEnrollmentProfileMonitoringType>[] = [
    {
      accessorKey: 'means',
      header: 'Moyen de paiement',
      cell: ({ row }) => paymentMeansEnumMapping[row.original.means]
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: ({ row }) => <PaymentRecordStatusBadge paymentRecord={row.original} />
    }
  ]

  if (!paymentRecords) return <Loader />

  return (
    <div>
      <EditableInnerBox>
        <div className="flex justify-between gap-4 mobile:gap-3 mobile:flex-col mobile:items-start">
          {enrollmentStatus.hasPayment && (
            <div className="flex-1 mobile:w-full">
              <div className="flex items-center gap-3">
                <EditableInnerBoxTitle title="Détails des paiements" />
                <Button
                  variant="primary"
                  label="Rembourser"
                  onClick={() => {
                    toggleModal('refund')
                  }}
                />
              </div>
              <div className="h-3" />
              <EnrollmentPaymentRecord.PaymentSummary enrollmentId={enrollment.id} />
              <div className="h-2" />
              <div>
                <Typography variant="tag" className="text-grey">
                  Paiements effectués
                </Typography>
                <div className="h-1" />
                <EnrollmentProfileMonitoring.PaymentTable
                  type="finished"
                  records={paymentRecords.data.filter(
                    (record) =>
                      record.status !== StatusEnum.PENDING &&
                      record.type !== PaymentTypeEnum.BANK_IMPRINT
                  )}
                  columns={columns}
                />
              </div>
              <div className="h-2" />

              <div className="flex items-center gap-2">
                <Typography variant="tag" className="text-grey">
                  Paiements à venir
                </Typography>
                <div className="p-1 border border-grey-light rounded">
                  <Checkbox
                    label="Prélèvement automatique"
                    checked={enrollment.optinForAutomaticDebit}
                    loading={
                      isActivateAutomaticPaymentsPending || isDeactivateAutomaticPaymentsPending
                    }
                    onChange={() => {
                      const newValue = !enrollment.optinForAutomaticDebit
                      setEnrollment({ ...enrollment, optinForAutomaticDebit: newValue })
                      if (newValue) activateAutomaticPaymentsMutation()
                      else deactivateAutomaticPaymentsMutation()
                    }}
                    labelVariant="small"
                  />
                </div>
              </div>

              <div className="h-2" />
              <EnrollmentProfileMonitoring.PaymentTable
                type="pending"
                records={paymentRecords.data.filter(
                  (record) =>
                    record.status === StatusEnum.PENDING &&
                    record.type !== PaymentTypeEnum.BANK_IMPRINT
                )}
                columns={columns}
              />
            </div>
          )}

          <div className="w-96 mobile:w-full flex flex-col gap-3">
            {/* the pricing detail is read from the journey proposition; a One to One Individuel
                enrollment is priced from its own proposition instead */}
            {EnrollmentProperties.isOtoIndividual(enrollment) ? (
              <OtoIndividualEnrollmentPriceDetails
                enrollmentId={enrollment.id}
                title={<EditableInnerBoxTitle title="Détail du tarif (tout inclus)" />}
                className="bg-white-hover"
                fallbackOptionTitle={
                  getTranslatedName(enrollment.otoIndividualProposition?.option) || undefined
                }
              />
            ) : (
              enrollment.mainProposition && (
                <EnrollmentPropositionSummary
                  title={
                    <div className="flex items-center justify-between gap-2">
                      <EditableInnerBoxTitle
                        title="Détail du tarif (tout inclus)"
                        onEdit={() => toggleModal('summary')}
                      />

                      {enrollmentStatus.hasPayment && (
                        <Button
                          size="small"
                          variant="ghost"
                          label="+ Ajouter un ajustement"
                          className="whitespace-nowrap"
                          onClick={() => toggleModal('tariffAdjustment')}
                        />
                      )}
                    </div>
                  }
                  readonly
                  enrollmentId={enrollment.id}
                  propositionId={enrollment.mainProposition.id}
                  className="bg-white-hover"
                  onInputChange={async () => {}}
                />
              )
            )}
          </div>
        </div>
      </EditableInnerBox>
      <div className="h-3" />

      <EditableInnerBox title="Documents">
        <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1">
          <ResourceAccessItem
            mode="pdf"
            label="Facture - confirmation d'inscription"
            disabled={!officialDocumentsAvailability.confirmationInvoice}
            showTooltipIcon={!officialDocumentsAvailability.confirmationInvoice}
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generateConfirmationInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
          {officialDocumentsAvailability.cancellationInvoice && (
            <ResourceAccessItem
              mode="pdf"
              label="Facture de frais - Annulation séjour"
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.enrollments.generateCancellationInvoice.url, {
                    id: enrollment.id
                  })
                })
              }
            />
          )}
          <ResourceAccessItem
            mode="pdf"
            label="Facture acquittée"
            disabled={!officialDocumentsAvailability.paidInvoice}
            showTooltipIcon={!officialDocumentsAvailability.paidInvoice}
            tooltipContent="Ce document sera disponible à compter du premier jour de séjour de votre enfant"
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generatePaidInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
          <ResourceAccessItem
            mode="pdf"
            label="Attestation de participation"
            disabled={!officialDocumentsAvailability.participationInvoice}
            showTooltipIcon={!officialDocumentsAvailability.participationInvoice}
            tooltipContent="Ce document sera disponible à compter du premier jour de séjour de votre enfant"
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generateParticipationInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
        </div>
      </EditableInnerBox>
      <div className="h-3" />

      <EditableInnerBox title="Historique administratif">
        <EnrollmentProfileMonitoring.ActionsHistory data={actions?.data ?? []} />
      </EditableInnerBox>

      <Modal
        isOpen={modalState.summary}
        title={'Ajouter une réduction'}
        hideActions
        icon={PriceTagIcon}
        onCancel={() => closeModel('summary')}
      >
        {/* the reduction form edits the journey proposition, which a One to One Individuel
            enrollment does not have */}
        {enrollment.mainProposition && (
          <PropositionReductionForm
            id={enrollment.mainProposition.id}
            enrollmentId={enrollment.id}
            defaultValues={mapEnrollmentPropositionToInput(enrollment.mainProposition, {
              cseCode: enrollment.cseRegistration?.cse?.code
            })}
            className={MODAL_CONTENT_CLASSNAME}
            onCancel={() => closeModel('summary')}
            onSuccess={(data) => {
              setEnrollment({ ...enrollment, mainProposition: data })
            }}
          />
        )}
      </Modal>

      <Modal
        isOpen={modalState.refund}
        title={'Rembourser'}
        hideActions
        icon={BackIcon}
        onCancel={() => closeModel('refund')}
      >
        <RefundEnrollmentForm
          enrollmentId={enrollment.id}
          onCancel={() => closeModel('refund')}
          onSuccess={({ result }) => {
            refetchPaymentRecords()
            refetchActions()
            notify(['enrollment-overpayment', 'enrollment-payment-record'])
            closeModel('refund')

            // the diffusion popup is only offered once the money was actually refunded
            if (result === paymentTransactionStatusEnum.SUCCEEDED)
              toggleModal('email-notification')
            else toast.error(REFUND_FAILED_MESSAGE)
          }}
        />
      </Modal>

      <EmailModal<{ includeParents: boolean }>
        emailApiEndpoint={
          constructApiUrl(API.enrollments.sendRefundNotification.url, {
            id: enrollment.id
          }) as ApiUrls
        }
        customCheckbox={{ label: 'Envoyer la facture de modification aux parents' }}
        label="Vous avez modifié certains champs du bulletin. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['email-notification']}
        closeModal={() => closeModel('email-notification')}
        beforeSubmit={(input, includeParents) => ({ ...input, includeParents })}
      />

      <Modal
        isOpen={modalState.tariffAdjustment}
        title="Ajouter un ajustement"
        hideActions
        icon={FilterTwoIcon}
        onCancel={() => closeModel('tariffAdjustment')}
      >
        {/* a tariff adjustment targets the journey proposition */}
        {enrollment.mainProposition && (
          <TariffAdjustmentForm
            enrollmentId={enrollment.id}
            propositionId={enrollment.mainProposition.id}
            onCancel={() => closeModel('tariffAdjustment')}
            onSuccess={() => {
              refetchActions()
              refetchPaymentRecords()
              notify([
                'enrollment-proposition-summary',
                'enrollment-payment-record',
                'enrollment-profile-monitoring-payment-record',
                'enrollment-overpayment'
              ])
              closeModel('tariffAdjustment')
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default EnrollmentProfileMonitoring

type PaymentRecordPaymentTable = Pick<
  PaymentRecord,
  'id' | 'amount' | 'type' | 'dateDue' | 'dateEffective' | 'cardNumber' | 'status' | 'means'
>

type PaymentTableProps = {
  records: PaymentRecordPaymentTable[]
  type: 'pending' | 'finished'
  columns?: Column<PaymentRecordPaymentTable>[]
}

EnrollmentProfileMonitoring.PaymentTable = ({ records, type, columns }: PaymentTableProps) => {
  const defaultColumns: Column<PaymentRecordPaymentTable>[] = [
    {
      accessorKey: 'amount',
      header: 'Montant',
      cell: ({ row }) => {
        if (row.original.type === PaymentTypeEnum.REFUND)
          return (
            <span className="text-red">
              -{NumberFormatter.formatCurrency(row.original.amount)}
            </span>
          )
        return NumberFormatter.formatCurrency(row.original.amount)
      }
    },
    type === 'pending'
      ? {
          accessorKey: 'dateDue',
          header: "Date d'échéance",
          cell: ({ row }) => (
            <Badge
              variant="grey"
              size="small"
              text={DateFormatter.DDMMYYYY(row.original.dateDue)}
            />
          )
        }
      : {
          // a settled record shows the day the money was actually paid, not its planned due date
          accessorKey: 'dateEffective',
          header: 'Date de paiement',
          cell: ({ row }) =>
            row.original.dateEffective ? (
              <Badge
                variant="grey"
                size="small"
                text={DateFormatter.DDMMYYYY(row.original.dateEffective)}
              />
            ) : (
              '-'
            )
        }
  ]
  return (
    <DataTable<PaymentRecordPaymentTable>
      columns={[...defaultColumns, ...(columns || [])]}
      data={records}
      rowCount={records.length}
      paginated={false}
      bodyVariant="white"
    />
  )
}

type ActionsHistoryProps = {
  data: EnrollmentAction[]
}

const CANCELLATION_WITH_EMAIL_TYPES = [
  EnrollmentActionTypeEnum.CANCEL_ENROLLMENT_RAD_PASSED,
  EnrollmentActionTypeEnum.CANCEL_ENROLLMENT_DIFFUSION_DEADLINE_PASSED,
  EnrollmentActionTypeEnum.CANCEL_ENROLLMENT_TRIP_FOLDER_LOCKED
]

function EnrollmentActionMotif({ action }: { action: EnrollmentAction }) {
  const { type, modifications } = action

  if (type === EnrollmentActionTypeEnum.CREATE_ENROLLMENT_VIA_INTERNET) {
    return (
      <div className={'w-7 h-7 bg-primary-light rounded flex items-center justify-center'}>
        <GlobeIcon color={tailwindColors.primary.DEFAULT} />
      </div>
    )
  }

  if (type === EnrollmentActionTypeEnum.CREATE_ENROLLMENT_VIA_LEC) {
    return <EmergencyPhoneIcon color={tailwindColors.primary.DEFAULT} />
  }

  if (type === EnrollmentActionTypeEnum.REFUND_ENROLLMENT) {
    return (
      <div className={'flex flex-col gap-1'}>
        {modifications?.map((m) => {
          if (!m.newValue) return null

          if (m.fieldName === 'refundStatus')
            return (
              <Typography key={m.fieldName} variant={'secondary-body'}>
                {`• Statut : ${statusEnumMapping[m.newValue as StatusEnum]?.label ?? m.newValue}`}
              </Typography>
            )

          if (m.fieldName === 'refundRequestedAmount')
            return (
              <Typography key={m.fieldName} variant={'secondary-body'}>
                {`• Montant demandé : ${NumberFormatter.formatCurrency(parseFloat(m.newValue))}`}
              </Typography>
            )

          return (
            <Typography key={m.fieldName} variant={'secondary-body'}>
              {`• Montant remboursé : ${NumberFormatter.formatCurrency(parseFloat(m.newValue))} ${m.customLabel ? `(${m.customLabel})` : ''}`}
            </Typography>
          )
        })}
      </div>
    )
  }

  if (type === EnrollmentActionTypeEnum.ADD_TARIFF_ADJUSTMENT) {
    return (
      <div className={'flex flex-col gap-1'}>
        {modifications?.map((m) => {
          if (!m.newValue) return null
          const oldTotal = m.oldValue
            ? NumberFormatter.formatCurrency(parseFloat(m.oldValue))
            : null
          const newTotal = NumberFormatter.formatCurrency(parseFloat(m.newValue))
          const diff = parseFloat(m.newValue) - (m.oldValue ? parseFloat(m.oldValue) : 0)
          const amount = Math.abs(diff)
          const label = diff > 0 ? 'Ajustement à la hausse' : 'Ajustement à la baisse'
          return (
            <Typography key={m.fieldName} variant={'secondary-body'}>
              {`• ${label} : ${NumberFormatter.formatCurrency(amount)}${m.customLabel ? ` (${m.customLabel})` : ''}${oldTotal ? ` — Total : ${oldTotal} → ${newTotal}` : ''}`}
            </Typography>
          )
        })}
      </div>
    )
  }

  if (CANCELLATION_WITH_EMAIL_TYPES.includes(type)) {
    const emails =
      modifications?.flatMap((m) =>
        (m.newValue ?? m.customLabel ?? '')
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean)
      ) ?? []
    return (
      <div className={'flex flex-col gap-1'}>
        <Typography variant={'secondary-body'}>Emails envoyés à :</Typography>
        <div className={'flex flex-wrap gap-1'}>
          {emails.length > 0
            ? emails.map((email, index) => (
                <Badge key={`${email}-${index}`} variant={'white'} text={email} />
              ))
            : '-'}
        </div>
      </div>
    )
  }

  if (!modifications || modifications.length === 0) {
    return <>-</>
  }

  return (
    <>
      {modifications.map((m) => {
        const emptyValue = enrollmentFieldEmptyValues[m.fieldName]
        const renderValue = (value?: string) =>
          emptyValue && !value ? emptyValue : valueRender(value)
        return (
          <Typography key={m.fieldName} variant={'secondary-body'}>
            {`• ${enrollmentFieldNames[m.fieldName]} : ${renderValue(m.oldValue)} -> ${renderValue(m.newValue)}`}
          </Typography>
        )
      })}
    </>
  )
}

EnrollmentProfileMonitoring.ActionsHistory = ({ data }: ActionsHistoryProps) => {
  const columns: Column<EnrollmentAction>[] = [
    {
      accessorKey: 'type',
      header: 'Action',
      enableSorting: true,
      cell: ({ row }) => enrollmentActionTypeEnumMapping[row.original.type]
    },
    {
      accessorKey: 'createdAt',
      header: 'Date et Heure',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant="grey"
          size="small"
          text={DateFormatter.ddMMYYYY_HHMM(row.original.createdAt)}
        />
      )
    },
    {
      accessorKey: 'createdBy.firstName',
      header: 'Utilisateur',
      enableSorting: true,
      cell: ({ row }) =>
        valueRender(row.original.createdBy, (u) => toFullName(u.firstName, u.lastName))
    },
    {
      accessorKey: 'id',
      header: 'Motif',
      cell: ({ row }) => (
        <div className={'flex flex-col gap-1 py-2'}>
          <EnrollmentActionMotif action={row.original} />
        </div>
      )
    }
  ]
  return (
    <DataTable<EnrollmentAction>
      columns={columns}
      data={data}
      rowCount={data.length}
      paginated={false}
      bodyVariant={'white'}
      manualSorting={false}
    />
  )
}
